// docs/66 RM12: the gallery's fight stage — what `stage_fight` in fk-sim-wasm returns (the forge's
// `deploy::Stage`), read into the client's shapes, and the picker's lists. GALLERY-ONLY: a live
// fight never needs the plan, only the board and the units on it.
import { boardFromWasmObj } from '../../core/sim/adapt';
import type { Snapshot, Tile } from '../../core/sim/types';

type Obj = Record<string, unknown>;

/** The nine setups (`fk_schema::Setup::key`). */
export const SETUPS = ['Clash', 'Defend', 'Escort', 'Ambushed', 'Assault', 'Rescue', 'Raid', 'Hunt', 'Crossing'] as const;
/** The ten grounds (`fk_schema::Ground`). */
export const GROUNDS = ['Mire', 'Greenwood', 'Autumnwood', 'Pinewood', 'Snowfield', 'Dunes', 'Highland', 'Jungle', 'Underdark', 'Ashlands'] as const;
/** Party sizes the picker offers. */
export const PARTIES = [1, 2, 4, 8, 20] as const;

export interface FightEntry { tiles: Tile[]; from: string; feature?: string }
export interface FightStage {
  board: Snapshot['board'];
  heroSpots: Tile[]; leader: Tile;
  zones: { heroes: Tile[]; foes: Tile[][]; ward: Tile[]; landmark: Tile[]; thing: Tile[] };
  entries: FightEntry[];
  recipe: string; setup: string; proof: string; dir: number;
  marks: Array<{ feature: string; tiles: Tile[] }>;
}

const tile = (t: unknown): Tile => ({ x: Number((t as Obj)['x'] ?? 0), y: Number((t as Obj)['y'] ?? 0) });
const tiles = (v: unknown): Tile[] => ((v as unknown[] | undefined) ?? []).map(tile);
const name = (v: unknown): string => (typeof v === 'string' ? v : v && typeof v === 'object' ? Object.keys(v as Obj)[0] ?? '' : '');

export function fightStageFromWasm(s: Obj): FightStage {
  const z = (s['zones'] as Obj | undefined) ?? {};
  const board = boardFromWasmObj(s['board'] as Obj);
  if (s['kit']) board.kit = String(s['kit']);
  return {
    board,
    heroSpots: tiles(s['hero_spots']), leader: tile(s['leader_tile']),
    zones: { heroes: tiles(z['heroes']), foes: ((z['foes'] as unknown[] | undefined) ?? []).map(tiles), ward: tiles(z['ward']), landmark: tiles(z['landmark']), thing: tiles(z['thing']) },
    entries: ((s['entries'] as Obj[] | undefined) ?? []).map((e) => ({ tiles: tiles(e['tiles']), from: String(e['from'] ?? ''), ...(e['feature'] ? { feature: name(e['feature']) } : {}) })),
    recipe: name(s['recipe']), setup: name(s['setup']), proof: name(s['proof']), dir: Number((s['frame'] as Obj | undefined)?.['dir'] ?? 0),
    marks: ((s['marks'] as Array<[unknown, unknown]> | undefined) ?? []).map(([f, t]) => ({ feature: name(f), tiles: tiles(t) })),
  };
}

/** The recipe keys (`fight_recipes`) a ground has, as `[key, grounds]` pairs → keys for `ground`. */
export function recipesFor(all: ReadonlyArray<[string, string[]]>, ground: string): string[] {
  return all.filter(([, gs]) => gs.includes(ground)).map(([k]) => k);
}

/** The compass word for where a square lies on the board (the heroes' side, for the caption). */
export function sideOf(t: Tile, w: number, h: number): string {
  const x = (t.x + 0.5) / w - 0.5, y = (t.y + 0.5) / h - 0.5;
  const ns = y < -0.17 ? 'north' : y > 0.17 ? 'south' : '';
  const ew = x < -0.17 ? 'west' : x > 0.17 ? 'east' : '';
  return ns && ew ? `${ns}-${ew}` : ns || ew || 'middle';
}
